import { useState } from "react";
import { Search, Loader2, Phone, ArrowDownLeft, ArrowUpRight, Image as ImageIcon, FileText, Mic, Video, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { whatsappApi } from "@/lib/crmApi";
import { cn } from "@/lib/utils";

/**
 * Phone lookup tool — admin types ANY phone number (any format) and gets
 * the COMPLETE chronological feed of every WhatsApp message ever exchanged
 * with that number. Reaches across leads, LIDs, and phone_pn variants.
 *
 * The original Inbox shows conversations grouped by current contact ID;
 * this dialog is the "give me everything for this number, no matter how it
 * was tagged" tool used for monitoring / audit.
 */

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const mediaIcon = (type) => {
  if (type === "image") return <ImageIcon className="h-3 w-3" />;
  if (type === "video") return <Video className="h-3 w-3" />;
  if (type === "audio") return <Mic className="h-3 w-3" />;
  if (type === "document") return <FileText className="h-3 w-3" />;
  return null;
};

export default function PhoneLookupDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    const digits = query.replace(/[^0-9]/g, "");
    if (digits.length < 6) {
      setError("Numéro trop court (au moins 6 chiffres)");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await whatsappApi.byPhone(digits, { limit: 500 });
      setData(result);
    } catch (e2) {
      setError(
        e2?.response?.data?.detail || e2?.humanMessage || "Erreur de recherche"
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setQuery("");
    setData(null);
    setError(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[90vh] sm:max-w-3xl overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-emerald-600" />
            Rechercher toutes les conversations par numéro
          </DialogTitle>
          <DialogDescription>
            Saisis un numéro WhatsApp dans n'importe quel format. On agrège
            <b> tous les messages</b> liés à ce numéro — peu importe le lead,
            le LID ou le format d'enregistrement.
          </DialogDescription>
        </DialogHeader>

        {/* Search bar */}
        <form onSubmit={search} className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ex : +221 77 555 12 34, 221775551234, 0775551234…"
              className="pl-9 font-mono tabular-nums"
              data-testid="phone-lookup-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading || !query.trim()}
            className="gap-1.5 transition-transform active:scale-[0.96] disabled:active:scale-100"
            data-testid="phone-lookup-search-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Recherche…
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                Chercher
              </>
            )}
          </Button>
        </form>

        {/* Error banner */}
        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            ⚠️ {String(error)}
          </div>
        )}

        {/* Result body */}
        {data && (
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Summary bar */}
            <div className="grid grid-cols-3 gap-3 rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Messages totaux
                </p>
                <p className="font-display text-xl font-medium tabular-nums">
                  {data.total}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Reçus / Envoyés
                </p>
                <p className="font-display text-xl font-medium tabular-nums">
                  <span className="text-blue-600">{data.in_count}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-emerald-600">{data.out_count}</span>
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Leads associés
                </p>
                <p className="font-display text-xl font-medium tabular-nums">
                  {Object.keys(data.leads || {}).length || "—"}
                </p>
              </div>
            </div>

            {/* Linked leads */}
            {Object.keys(data.leads || {}).length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Leads :
                </span>
                {Object.values(data.leads).map((lead) => (
                  <a
                    key={lead.id}
                    href={`/leads/${lead.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/60"
                  >
                    {lead.nom}
                    {lead.programme_immobilier && (
                      <span className="text-emerald-500/70">
                        · {lead.programme_immobilier}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            )}

            {/* Chronological feed */}
            <div className="mt-3 flex-1 overflow-y-auto rounded-xl border border-border/60 bg-card p-3">
              {data.items.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Aucun message échangé avec ce numéro.
                </div>
              ) : (
                <ul className="space-y-2">
                  {data.items.map((m, i) => {
                    const isIn = m.direction === "in";
                    const lead = m.lead_id ? data.leads[m.lead_id] : null;
                    return (
                      <li
                        key={m.id || `${m.timestamp}-${i}`}
                        className={cn(
                          "flex max-w-[85%] flex-col gap-1 rounded-xl px-3 py-2 text-sm",
                          isIn
                            ? "bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-100"
                            : "ml-auto bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
                        )}
                      >
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-70">
                          {isIn ? (
                            <ArrowDownLeft className="h-3 w-3" />
                          ) : (
                            <ArrowUpRight className="h-3 w-3" />
                          )}
                          {isIn ? "Reçu" : "Envoyé"}
                          <span className="font-mono tabular-nums opacity-80">
                            · {fmtTime(m.timestamp)}
                          </span>
                          {lead && (
                            <span className="ml-auto truncate rounded bg-white/60 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-foreground/70 dark:bg-zinc-900/60">
                              {lead.nom}
                            </span>
                          )}
                          {m.media_type && (
                            <span className="inline-flex items-center gap-0.5 rounded bg-white/60 px-1 py-px text-[9px] dark:bg-zinc-900/60">
                              {mediaIcon(m.media_type)}
                              {m.media_type}
                            </span>
                          )}
                        </div>
                        <div className="whitespace-pre-wrap break-words leading-snug">
                          {m.body || (
                            <span className="italic opacity-60">
                              [{m.media_type || "média"} sans légende]
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Empty state on first open */}
        {!data && !loading && !error && (
          <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 text-sm text-muted-foreground">
            <Search className="h-6 w-6 opacity-40" />
            Saisis un numéro et clique « Chercher ».
            <p className="text-[11px] text-muted-foreground/70">
              Marche avec tous les formats : <code className="rounded bg-muted px-1">+221 77 ...</code>,{" "}
              <code className="rounded bg-muted px-1">221775551234</code>,{" "}
              <code className="rounded bg-muted px-1">775551234</code>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
